import { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';

import { useLoyaltyServiceUserLazyQuery, useLoyaltyUserLazyQuery } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

import { mapLegacyLoyaltyUser, mapServiceLoyaltyUser } from './loyalty-user-adapters';
import { LoyaltyUser, LoyaltyUserQueryVariables } from './types';

type LoyaltyUserSource = 'legacy' | 'service';

interface LoyaltyUserQueryState {
  called: boolean;
  error?: ApolloError;
  identity?: string;
  loading: boolean;
  loyaltyUser?: LoyaltyUser;
  source?: LoyaltyUserSource;
}

const INITIAL_QUERY_STATE: LoyaltyUserQueryState = {
  called: false,
  loading: false,
};

const toApolloError = (error: unknown): ApolloError =>
  error instanceof ApolloError
    ? error
    : new ApolloError({
        errorMessage: error instanceof Error ? error.message : String(error),
      });

export const useLoyaltyUserQuery = () => {
  const { loyaltyUserId } = useLoyaltyUserId();
  const useService = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_USER));
  const source: LoyaltyUserSource = useService ? 'service' : 'legacy';
  const [executeLegacyQuery] = useLoyaltyUserLazyQuery({
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });
  const [executeServiceQuery] = useLoyaltyServiceUserLazyQuery({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const [queryState, setQueryState] = useState<LoyaltyUserQueryState>(INITIAL_QUERY_STATE);
  const requestGeneration = useRef(0);
  const abortController = useRef<AbortController | null>(null);
  const activeIdentity = useRef(loyaltyUserId);
  const activeSource = useRef(source);
  activeIdentity.current = loyaltyUserId;
  activeSource.current = source;

  const cancelRequest = useCallback(() => {
    requestGeneration.current += 1;
    abortController.current?.abort();
    abortController.current = null;
  }, []);

  useEffect(() => {
    cancelRequest();
    setQueryState(INITIAL_QUERY_STATE);
    return cancelRequest;
  }, [cancelRequest, loyaltyUserId, source]);

  const fetchLoyaltyUser = useCallback(
    async (variables?: LoyaltyUserQueryVariables): Promise<LoyaltyUser> => {
      const identity = variables?.loyaltyId ?? activeIdentity.current;
      const requestSource = activeSource.current;

      if (!identity || (requestSource === 'service' && identity !== activeIdentity.current)) {
        return undefined;
      }

      const generation = requestGeneration.current + 1;
      requestGeneration.current = generation;
      abortController.current?.abort();
      const requestAbortController = new AbortController();
      abortController.current = requestAbortController;

      setQueryState({
        called: true,
        error: undefined,
        identity,
        loading: true,
        loyaltyUser: undefined,
        source: requestSource,
      });

      const isCurrentRequest = () =>
        requestGeneration.current === generation &&
        !requestAbortController.signal.aborted &&
        activeIdentity.current === identity &&
        activeSource.current === requestSource;

      try {
        let loyaltyUser: LoyaltyUser;
        if (requestSource === 'service') {
          const result = await executeServiceQuery({
            context: {
              fetchOptions: { signal: requestAbortController.signal },
              queryDeduplication: false,
            },
          });
          if (result.error) {
            throw result.error;
          }
          loyaltyUser = mapServiceLoyaltyUser(result.data?.loyaltyServiceUser);
        } else {
          const result = await executeLegacyQuery({ variables: { loyaltyId: identity } });
          if (result.error) {
            throw result.error;
          }
          loyaltyUser = mapLegacyLoyaltyUser(result.data?.loyaltyUserV2);
        }

        if (!isCurrentRequest()) {
          return undefined;
        }

        setQueryState({
          called: true,
          error: undefined,
          identity,
          loading: false,
          loyaltyUser,
          source: requestSource,
        });
        return loyaltyUser;
      } catch (error) {
        if (!isCurrentRequest()) {
          return undefined;
        }

        const apolloError = toApolloError(error);
        setQueryState({
          called: true,
          error: apolloError,
          identity,
          loading: false,
          loyaltyUser: undefined,
          source: requestSource,
        });
        throw apolloError;
      }
    },
    [executeLegacyQuery, executeServiceQuery]
  );

  const stateMatchesSelection =
    queryState.identity === loyaltyUserId && queryState.source === source;

  return {
    called: stateMatchesSelection ? queryState.called : false,
    error: stateMatchesSelection ? queryState.error : undefined,
    fetchLoyaltyUser,
    loading: stateMatchesSelection ? queryState.loading : false,
    loyaltyUser: stateMatchesSelection ? queryState.loyaltyUser : undefined,
    source,
  };
};
